





















































































import { Component, Prop, Vue, Watch, Ref } from "vue-property-decorator";
import store from "@/store";
import moment from "moment";

//  @types
import { CdsDataQuery } from "@/types/cdsDataQuery";
import { LogDataFilter } from "@/types/logDataFilter";
import { FeatureData } from "@/types/cds/featureData";
import { LogData } from "@/types/logData";
//  @components
import { Chart } from "highcharts-vue";
import Highcharts from "highcharts";
import HighchartsNoData from "highcharts/modules/no-data-to-display";
HighchartsNoData(Highcharts);
//  @clients
import LogDataClient from "Clients/log-data-client";
//  @modules
import { getModule } from "vuex-module-decorators";
import LogVariablesModule from "@/store/clients/LogVariables.module";
import CdsModule from "@/store/clients/Cds.module";
import { apiGet } from "@/services/api";

const LogVariables = getModule(LogVariablesModule, store);
const Cds = getModule(CdsModule, store);

export interface LoggingHistoryGraphQueryData {
  logDataFilter: LogDataFilter | undefined;
  cdsDataQuery: CdsDataQuery | undefined;
}

@Component({
  components: {
    HighCharts: Chart,
  },
})
export default class DataHistoryChart extends Vue {
  @Prop({ type: Object }) readonly queryData?: LoggingHistoryGraphQueryData;
  @Ref() readonly chart!: any;
  //  @Data
  fetchDataError = false;
  graphZoomed = false;
  logData: LogData | null = null;
  featureData: FeatureData | null = null;
  loadingState: "idle" | "loading" = "idle";
  selectedXAxisMin = null;
  selectedXAxisMax = null;
  differenceThreshold = 55;
  logVariableCount = 0;
  cdsFeatureCount = 0;

  // @Watchers
  @Watch("queryData", { immediate: true })
  async onLogDataFilterChange(queryData: LoggingHistoryGraphQueryData): Promise<void> {
    this.loadingState = "loading";
    this.graphZoomed = false;
    this.logData = null;
    this.featureData = null;
    this.logVariableCount = 0;
    this.cdsFeatureCount = 0;
    this.fetchDataError = false;

    try {
      if (queryData.logDataFilter) {
        const filter = queryData.logDataFilter;
        this.logVariableCount = filter.logVariableIds.split(",").length;
        if (this.logVariableCount > 0) {
          this.logData = await LogDataClient.batchFindLogData(filter.logVariableIds, filter.fromDate, filter.toDate, filter.granularity, filter.filterMinMax, filter.filterOutliers);
        }
      }
      if (queryData.cdsDataQuery) {
        const query = queryData.cdsDataQuery;
        this.cdsFeatureCount = query.cdsFeatureIds.split(",").length;
        if (this.cdsFeatureCount > 0) {
          this.featureData = await apiGet<FeatureData>(
            `/Cds/Data?vesselId=${query.vesselId}&featureIds=${query.cdsFeatureIds}&granularity=${query.granularity}&fromDate=${query.fromDate}&toDate=${query.toDate}&filterOutliers=${query.filterOutliers}`
          );
        }
      }
    } catch (e) {
      this.fetchDataError = true;
    } finally {
      this.loadingState = "idle";
    }
  }

  // @Getters
  get isDataLoading(): boolean {
    return this.loadingState === "loading";
  }

  get hasLogData(): boolean {
    return this.logData !== null || this.featureData !== null;
  }

  get isSingleSeries(): boolean {
    return (this.logVariableCount === 1 && this.cdsFeatureCount === 0) || (this.logVariableCount === 0 && this.cdsFeatureCount === 1);
  }

  get singleSeriesLegend(): string {
    if (this.logVariableCount === 1 && this.logData) {
      return (this.getLogVariableName(0) || "") + this.averageValue(0, false);
    }
    if (this.cdsFeatureCount === 1 && this.featureData) {
      return (this.getFeatureName(0) || "") + this.averageValue(0, true);
    }
    return "";
  }

  // Highcharts only provide built in colors for the x-axis, so here we manually set the colors of the y-axis to match.
  getSeriesColor(index: number): string {
    // Get the actual color that Highcharts will assign to this series
    // Highcharts default colors cycle through this array
    const defaultColors = [
      this.themeColor("BackgroundBrandSecondary"),
      this.themeColor("BorderInfoPrimary"),
      this.themeColor("BorderBrandPrimary"),
      this.themeColor("BackgroundPositivePrimary"),
      this.themeColor("BorderWarningPrimary"),
    ];
    return defaultColors[index % defaultColors.length];
  }

  get yAxis(): any {
    let axes = this.logData?.filter.logVariableIds.map((_, index) => {
      return {
        opposite: false,
        title: {
          text: "",
          style: { color: this.getSeriesColor(index) },
        },
        labels: {
          formatter: function (this: any) {
            const value = this.value;
            const formattedValue = Math.abs(value) >= 1000 ? (value / 1000).toFixed(value % 1000 === 0 ? 0 : 1) + "k" : value;
            return formattedValue + " " + (this.axis.userOptions.unit || "");
          },
          style: { color: this.getSeriesColor(index) },
        },
        gridLineColor: this.themeColor("ContentInverseSecondaryStatic"),
        unit: this.logData?.units[index]?.name || "", // Store unit for formatter access
        offset: index > 0 ? index * 70 : 0, // Add offset for axes beyond first 2
        ...this.yAxisMinMaxValues(index),
      };
    });
    axes = axes ?? [];
    const featureDataAxes = this.featureData?.filter.featureIds.map((_, index) => {
      const totalIndex = index + this.logVariableCount;
      return {
        opposite: false,
        title: {
          text: "",
          style: { color: this.getSeriesColor(totalIndex) },
        },
        labels: {
          formatter: function (this: any) {
            const value = this.value;
            const formattedValue = Math.abs(value) >= 1000 ? (value / 1000).toFixed(value % 1000 === 0 ? 0 : 1) + "k" : value;
            return formattedValue + " " + (this.axis.userOptions.unit || "");
          },
          style: { color: this.getSeriesColor(totalIndex) },
        },
        gridLineColor: this.themeColor("ContentInverseSecondaryStatic"),
        unit: this.featureData?.units[index]?.name || "", // Store unit for formatter access
        offset: totalIndex > 0 ? totalIndex * 70 : 0, // Add offset for axes beyond first 2
        ...this.featureDataYAxisMinMaxValues(index),
      };
    });
    axes.push(...(featureDataAxes ?? []));
    return this.combineCloseAxises(axes);
  }

  get chartOptions(): unknown {
    return {
      time: {
        useUTC: true,
      },
      credits: {
        enabled: false,
      },
      colors: [
        this.themeColor("BackgroundBrandSecondary"),
        this.themeColor("BorderInfoPrimary"),
        this.themeColor("BorderBrandPrimary"),
        this.themeColor("BackgroundPositivePrimary"),
        this.themeColor("BorderWarningPrimary"),
      ],
      chart: {
        marginTop: 50,
        type: "line",
        zoomType: "x",
        backgroundColor: this.themeColor("BackgroundElevatedPrimaryDefault"),
        resetZoomButton: {
          theme: {
            display: "none",
          },
        },
        events: {
          //Updates the average value in legend when zooming on the graph
          selection: function (this: any, event: any) {
            setTimeout(() => {
              if (event.resetSelection) {
                this.graphZoomed = false;
              } else {
                this.graphZoomed = true;
              }

              //Custom code to update averge value in label when zooming the graph
              this.selectedXAxisMin = event.target.xAxis[0].min;
              this.selectedXAxisMax = event.target.xAxis[0].max;

              const xAxisMin = Math.ceil(event.target.xAxis[0].min);
              const xAxisMax = Math.ceil(event.target.xAxis[0].max);
              const seriesList = event.target.series;
              const selectedSeriesList: any[] = seriesList.map((series: any) => {
                return series.data.filter((point: any) => point.x >= xAxisMin && point.x <= xAxisMax);
              });

              for (const i in selectedSeriesList) {
                const isCdsFeature = seriesList[i].userOptions.customMetaData.isCdsFeature;
                const index = isCdsFeature ? Number(i) - this.logVariableCount : i;
                const featureName = isCdsFeature ? this.getFeatureName(index) : this.getLogVariableName(i);
                const averageValue = this.averageValue(index, isCdsFeature, selectedSeriesList[i]);
                seriesList[i].update({ name: `${featureName} ${averageValue}` }, true);
              }
            });
          }.bind(this),
        },
        style: {
          fontFamily: "Helvetica Neue",
        },
      },
      legend: {
        enabled: !this.isSingleSeries,
        itemStyle: {
          color: this.themeColor("ContentTertiary"),
          fontWeight: "400",
        },
      },
      title: {
        text: "",
      },
      yAxis: this.yAxis,
      xAxis: {
        type: "datetime",
        showEmpty: false,
        minorTickLength: 0,
        tickLength: 0,
        lineColor: this.themeColor("ContentInverseSecondaryStatic"),
        labels: {
          style: {
            color: this.themeColor("ContentPrimary"),
          },
        },
      },
      tooltip: {
        backgroundColor: this.themeColor("BackgroundTooltip"),
        borderWidth: 0,
        borderRadius: 16,
        padding: 12,
        shadow: false,
        style: {
          color: "#EBEBEB",
        },
      },
      series: this.series,
      noData: {
        style: {
          color: "#331714",
        },
      },
      lang: {
        noData: "Found no data for selected time period",
      },
    };
  }

  get series(): any {
    // Build a map from original axis index to combined axis index
    const axisIndexMap = new Map<number, number>();
    const seenUnits = new Map<string, number>();
    let combinedIndex = 0;

    // Map log data axes
    this.logData?.filter.logVariableIds.forEach((_, index) => {
      const unitName = this.logData?.units[index]?.name || "";
      if (!seenUnits.has(unitName)) {
        seenUnits.set(unitName, combinedIndex);
        combinedIndex++;
      }
      axisIndexMap.set(index, seenUnits.get(unitName)!);
    });

    // Map feature data axes
    this.featureData?.filter.featureIds.forEach((_, index) => {
      const totalIndex = index + this.logVariableCount;
      const unitName = this.featureData?.units[index]?.name || "";
      if (!seenUnits.has(unitName)) {
        seenUnits.set(unitName, combinedIndex);
        combinedIndex++;
      }
      axisIndexMap.set(totalIndex, seenUnits.get(unitName)!);
    });

    let series = this.logData?.filter.logVariableIds.map((_, index) => ({
      name: this.getLogVariableName(index) + this.averageValue(index, false),
      lineWidth: 2,
      yAxis: axisIndexMap.get(index) ?? 0,
      color: this.getSeriesColor(index),
      data: this.dateValuePairs(index),
      customMetaData: {
        isCdsFeature: false,
      },
    }));
    series = series ?? [];
    const cdsFeatureSeries = this.featureData?.filter.featureIds.map((_, index) => ({
      name: this.getFeatureName(index) + this.averageValue(index, true),
      lineWidth: 2,
      yAxis: axisIndexMap.get(index + this.logVariableCount) ?? 0,
      color: this.getSeriesColor(index + this.logVariableCount),
      data: this.featureDateValuePairs(index),
      customMetaData: {
        isCdsFeature: true,
      },
    }));
    series.push(...(cdsFeatureSeries ?? []));
    return series;
  }

  // @Methods
  themeColor(color: string): string {
    return (this.$vuetify.theme.themes[this.$vuetify.theme.dark ? "dark" : "light"][color] as any) ?? this.$vuetify.theme.themes.light[color];
  }

  getLogVariableName(index: number): string | undefined {
    const logVariableId = this.logData?.filter.logVariableIds[index];
    if (logVariableId) {
      return LogVariables.currentVesselLogVariables.find(logVariable => logVariable.id === logVariableId)?.name;
    }
  }

  getLogVariableDisplayPrecision(index: number): number | undefined {
    const logVariableId = this.logData?.filter.logVariableIds[index];
    if (logVariableId) {
      return LogVariables.currentVesselLogVariables.find(logVariable => logVariable.id === logVariableId)?.displayPrecision;
    }
  }

  // Use the configured display precision when set, otherwise fall back to 2, capped at 2 decimals.
  // The DB value comes from the onboard InstDecimals and is often 0, which would otherwise truncate
  // real data to integers below the axis minimum and clip the whole line off the chart.
  getEffectiveDisplayPrecision(index: number): number {
    return Math.min(this.getLogVariableDisplayPrecision(index) || 2, 2);
  }

  getFeatureName(index: number): string | undefined {
    const featureId = this.featureData?.filter.featureIds[index];
    if (featureId) {
      return Cds.featureById(featureId)?.name;
    }
  }

  dateValuePairs(seriesIndex: number): any {
    const precision = this.getEffectiveDisplayPrecision(seriesIndex);
    return this.logData?.data
      ? Object.entries(this.logData.data)
          .map(([timestamp, pairValues]) => [this.parseAsUtc(timestamp), pairValues[seriesIndex] !== null ? this.truncateNumber(Number(pairValues[seriesIndex]), precision) : null])
          .filter(([x, y]) => x !== null && y !== null)
          .sort((a, b) => (a[0]! as number) - (b[0]! as number))
      : null;
  }

  // truncate number to `precision` decimals (no rounding, keeps shorter numbers as-is)
  truncateNumber(value: number, precision = 2): number {
    if (!Number.isFinite(value)) return value;
    const factor = Math.pow(10, precision);
    return value >= 0 ? Math.floor(value * factor) / factor : Math.ceil(value * factor) / factor;
  }

  featureDateValuePairs(seriesIndex: number): any {
    return this.featureData?.data
      ? Object.entries(this.featureData.data)
          .map(([timestamp, pairValues]) => [Date.parse(timestamp), pairValues[seriesIndex] !== null ? this.truncateNumber(Number(pairValues[seriesIndex]), 2) : null])
          .filter(([x, y]) => x !== null && y !== null)
          .sort((a, b) => (a[0]! as number) - (b[0]! as number))
      : null;
  }

  yAxisMinMaxValues(seriesIndex: number): { max: number; min: number; unit: string | undefined } | null {
    const yAxisValues = this.logData?.data
      ? Object.entries(this.logData.data).map(([timestamp, pairValues]) => (pairValues[seriesIndex] !== null ? this.truncateNumber(Number(pairValues[seriesIndex]), this.getEffectiveDisplayPrecision(seriesIndex)) : 0))
      : null;
    if (yAxisValues === null) return yAxisValues;
    return {
      min: Math.min(...yAxisValues) || 0,
      max: Math.max(...yAxisValues) || 100,
      unit: this.logData?.units[seriesIndex].name,
    };
  }

  featureDataYAxisMinMaxValues(seriesIndex: number): { max: number; min: number; unit: string | undefined } | null {
    const yAxisValues = this.featureData?.data
      ? Object.entries(this.featureData.data).map(([_, pairValues]) => (pairValues[seriesIndex] !== null ? this.truncateNumber(Number(pairValues[seriesIndex]), 2) : 0))
      : null;
    if (yAxisValues === null) return yAxisValues;
    return {
      min: Math.min(...yAxisValues) || 0,
      max: Math.max(...yAxisValues) || 100,
      unit: this.featureData?.units[seriesIndex].name,
    };
  }

  parseAsUtc(timestamp: string): number {
    if (!timestamp.endsWith("Z")) {
      timestamp += "Z";
    }
    return Date.parse(timestamp);
  }

  combineCloseAxises(axes: any): any[] {
    // Group axes by unit
    const unitGroups = new Map<string, any[]>();
    axes.forEach((axis: any) => {
      const unitName = axis.unit || "default";
      if (!unitGroups.has(unitName)) {
        unitGroups.set(unitName, []);
      }
      unitGroups.get(unitName)!.push(axis);
    });

    const combinedAxes: any[] = [];
    let globalOffset = 0;

    // For each group with same unit, sync their min/max but keep different offsets
    unitGroups.forEach((axesGroup, unitName) => {
      if (axesGroup.length >= 2) {
        // Collect all min/max values from axes with same unit
        const yAxesMinMaxValues: number[] = [];
        axesGroup.forEach((axis: any) => {
          yAxesMinMaxValues.push(axis?.min ?? 0, axis?.max ?? 0);
        });

        // Find the common min/max range
        const commonMin = Math.min(...yAxesMinMaxValues);
        const commonMax = Math.max(...yAxesMinMaxValues);

        // Take the first axis from the group and update it with combined min/max
        const combinedAxis = axesGroup[0];
        combinedAxis.min = commonMin;
        combinedAxis.max = commonMax;
        combinedAxis.offset = globalOffset;
        combinedAxes.push(combinedAxis);

        globalOffset += 70; // Increment offset for next unique unit
      } else {
        // Single axis with unique unit
        const axis = axesGroup[0];
        axis.offset = globalOffset;
        combinedAxes.push(axis);

        globalOffset += 70; // Increment offset for next unique unit
      }
    });

    return combinedAxes;
  }

  resetZoom(): void {
    this.chart.chart.zoomOut();
  }

  averageValue(index: number, isCdsFeature: boolean, data?: []): string {
    let counter = 0;
    let value = 0;

    //When zooming the graph, the method is provided with data for the zoomed area only
    if (data) {
      const dataArray = data;

      dataArray.forEach(function (dataEntry: any) {
        if (dataEntry.y !== null) {
          value += dataEntry.y;
          counter++;
        }
      });
    } else {
      const dataArray = isCdsFeature ? (this.featureData?.data ? Object.values(this.featureData.data) : []) : this.logData?.data ? Object.values(this.logData.data) : [];

      dataArray.forEach(function (dataEntry) {
        if (dataEntry[index] !== null) {
          value += dataEntry[index];
          counter++;
        }
      });
    }

    const unit = isCdsFeature ? this.featureData?.units[index].name : this.logData?.units[index].name;
    const displayPrecision = isCdsFeature ? 2 : this.getEffectiveDisplayPrecision(index);
    return counter > 0 ? ` (Avg: ${Number((value / counter).toFixed(displayPrecision))} ${unit})` : " (No data)";
  }

  updateTimePeriod(): void {
    this.$emit("updateTimePeriod", { fromDateTime: this.selectedXAxisMin, toDateTime: this.selectedXAxisMax });
  }
}
